import React, { HTMLAttributes, ReactElement } from 'react';
import cn from 'classnames';
import {
  CheckmarkCircleFillIcon,
  ExclamationmarkTriangleFillIcon,
  InformationSquareFillIcon,
  XMarkOctagonFillIcon,
} from '@navikt/aksel-icons';

import classes from './Alert.module.css';
import { Heading, HeadingProps } from '../Heading';

export type AlertVariants = 'info' | 'success' | 'warning' | 'danger';
export type AlertSizes = 'sm' | 'md' | 'lg';
type AlertProps = {
  title?: string;
  variant: AlertVariants;
  size: AlertSizes;
  headingSize?: HeadingProps['as'];
  children: React.ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const AlertColorVariantsMap: Record<AlertVariants, string> = {
  info: 'var(--info-light)',
  success: 'var(--success-light)',
  warning: 'var(--warning-light)',
  danger: 'var(--danger-light)',
};

const AlertDarkColorVariantsMap: Record<AlertVariants, string> = {
  info: 'var(--info-dark)',
  success: 'var(--success-dark)',
  warning: 'var(--warning-dark)',
  danger: 'var(--danger-dark)',
};

const AlertIconVariantsMap = (variant: AlertVariants): React.ReactNode => {
  const iconVariants = {
    info: (
      <InformationSquareFillIcon
        style={{ color: AlertDarkColorVariantsMap[variant] }}
      />
    ),
    success: (
      <CheckmarkCircleFillIcon
        style={{ color: AlertDarkColorVariantsMap[variant] }}
      />
    ),
    warning: (
      <ExclamationmarkTriangleFillIcon
        style={{ color: AlertDarkColorVariantsMap[variant] }}
      />
    ),
    danger: (
      <XMarkOctagonFillIcon
        style={{ color: AlertDarkColorVariantsMap[variant] }}
      />
    ),
  };

  return iconVariants[variant];
};

export const Alert = ({
  title,
  variant = 'info',
  size = 'sm',
  headingSize = 'h4',
  children,
  className: givenClassName,
  style = {},
  ...rest
}: AlertProps): ReactElement => {
  const className = cn(classes.container, classes[size], givenClassName);
  return (
    <div
      className={className}
      style={{
        ...style,
        backgroundColor: AlertColorVariantsMap[variant],
        borderLeft: `solid 4px ${AlertDarkColorVariantsMap[variant]}`,
      }}
      role="alert"
      {...rest}
    >
      <div>{AlertIconVariantsMap(variant)}</div>
      <div className={classes.alertTextContent}>
        {title && (
          <div className={classes.header}>
            <Heading as={headingSize}>{title}</Heading>
          </div>
        )}
        <div className={classes.content}>{children}</div>
      </div>
    </div>
  );
};
